/**
 * @fileoverview Organism: aggregate figures across every tracked habit.
 * @module components/organisms/StatsPanel
 */

import { Skeleton } from "@/components/atoms"
import { computeGlobalStats } from "@/lib/domain/stats"
import type { Habit, HabitEntry } from "@/types/types"

import "./StatsPanel.css"

/** Props accepted by {@link StatsPanel}. */
export interface StatsPanelProps {
	/** Every tracked habit; statistics are always across all of them. */
	readonly habits: readonly Habit[]
	/** Every logged entry. */
	readonly entries: readonly HabitEntry[]
	/** True until the first read settles. */
	readonly isLoading: boolean
}

/**
 * Aggregate totals: reps logged, today's progress, and the best streak.
 *
 * @param props - The collection, its entries and the loading state.
 * @returns The rendered panel, or a skeleton while loading. Renders nothing
 *   once loaded if there is nothing to summarise yet.
 */
export function StatsPanel({ habits, entries, isLoading }: StatsPanelProps) {
	if (isLoading) {
		return (
			<section className="stats" aria-label="Statistiques">
				<Skeleton width="100%" height="72px" radius="12px" />
			</section>
		)
	}

	if (habits.length === 0) return null

	const stats = computeGlobalStats(habits, entries)

	return (
		<section className="stats" aria-label="Statistiques">
			<div className="stats__grid">
				<div className="stats__tile">
					<strong>{stats.totalReps}</strong>
					<span>répétitions au total</span>
				</div>
				<div className="stats__tile">
					<strong>
						{stats.doneToday} / {stats.dueToday}
					</strong>
					<span>faites aujourd’hui</span>
				</div>
				<div className="stats__tile">
					<strong>{stats.habitCount}</strong>
					<span>habitudes suivies</span>
				</div>
				<div className="stats__tile">
					<strong>{stats.bestStreak?.streak ?? 0}</strong>
					<span>
						{stats.bestStreak === null
							? "meilleure série"
							: `série · ${stats.bestStreak.name}`}
					</span>
				</div>
			</div>
		</section>
	)
}
